// Batch 0 (UX/IA overhaul) — the ONE status pill for the whole app.
//
// Wraps the existing <Badge> and drives its tone from the canonical
// statusTone() map, and its label from the shared vocab (statusLabel). Pages
// stop hand-rolling per-file `statusVariant` records — they render
// <StatusBadge status={cam.status} /> and get consistent colour + casing
// everywhere. An optional dot gives the at-a-glance NOC read.

import { Badge } from "@/components/ui/badge";
import { statusTone, type StatusTone } from "@/lib/statusTone";
import { statusLabel } from "@/lib/vocab";
import { cn } from "@/lib/utils";

const TONE_TO_VARIANT: Record<
  StatusTone,
  "online" | "offline" | "warning" | "error" | "secondary"
> = {
  online: "online",
  offline: "offline",
  warning: "warning",
  error: "error",
  neutral: "secondary",
};

const DOT_CLASS: Record<StatusTone, string> = {
  online: "bg-emerald-500",
  offline: "bg-zinc-400",
  warning: "bg-amber-500",
  error: "bg-red-500",
  neutral: "bg-zinc-300",
};

export interface StatusBadgeProps {
  /** Raw status value from the API (e.g. "online", "degraded", "pending"). */
  status: string | null | undefined;
  /** Override the rendered label; defaults to the shared vocab label. */
  label?: string;
  /** Show a leading status dot. */
  dot?: boolean;
  className?: string;
}

export function StatusBadge({ status, label, dot, className }: StatusBadgeProps) {
  const tone = statusTone(status);
  const text = label ?? statusLabel(status ?? "unknown");
  return (
    <Badge variant={TONE_TO_VARIANT[tone]} className={cn("gap-1.5", className)}>
      {dot && (
        <span
          className={cn("h-1.5 w-1.5 rounded-full", DOT_CLASS[tone])}
          aria-hidden
        />
      )}
      {text}
    </Badge>
  );
}
